import { useEffect, useReducer } from 'react';

/*
 * The damage/repair cycle for one content panel.
 *
 * A panel degrades on its own clock, one stage at a time, and the clock stops
 * dead when it reaches the last one. Nothing gets worse while a crew is out.
 * The repair then runs on a separate, faster clock, walking the panel back
 * stage by stage; the crew only leaves once the panel is whole again.
 *
 * That "stops until the repair is done" rule is why this is a state machine and
 * not a keyframe sequence. A CSS animation cannot hold at a stage for an
 * unknown length of time, and the repair takes as long as the damage it found.
 */

export const LEVELS = ['perfect', 'light', 'medium', 'heavy', 'critical'];
export const CRITICAL = LEVELS.length - 1;

/*
 * Milliseconds. The crew phases are also written into CSS as transition
 * durations, so they are exported rather than duplicated -- a ship that moves
 * for longer than the state it is in would arrive after it had left.
 */
export const TIMING = {
  degrade: 5200, // between damage stages, while nobody is out
  summon: 1400, // the pause on critical before help is called
  inbound: 2600, // ship, off-screen to on station
  deploy: 2600, // worker, ship to plate
  repair: 2000, // between repair stages
  recover: 2400, // worker, plate back to ship
  outbound: 2600, // ship, off station to off-screen
};

const reducer = (state, action) => {
  switch (action.type) {
    case 'degrade':
      return { ...state, level: Math.min(state.level + 1, CRITICAL) };
    case 'repair':
      return { ...state, level: Math.max(state.level - 1, 0) };
    case 'crew':
      return { ...state, crew: action.crew };
    default:
      return state;
  }
};

const useRepairCycle = (active = true) => {
  const [state, dispatch] = useReducer(reducer, { level: 0, crew: 'away' });

  useEffect(() => {
    if (!active) return undefined;
    const { level, crew } = state;
    const go = (ms, action) => setTimeout(() => dispatch(action), ms);

    let timer;
    if (crew === 'away') {
      timer =
        level < CRITICAL
          ? go(TIMING.degrade, { type: 'degrade' })
          : // Critical: the damage clock is done. Call for a crew.
            go(TIMING.summon, { type: 'crew', crew: 'inbound' });
    } else if (crew === 'inbound') {
      timer = go(TIMING.inbound, { type: 'crew', crew: 'deploying' });
    } else if (crew === 'deploying') {
      timer = go(TIMING.deploy, { type: 'crew', crew: 'working' });
    } else if (crew === 'working') {
      // Works until there is nothing left to fix, however long that takes.
      timer =
        level > 0
          ? go(TIMING.repair, { type: 'repair' })
          : go(TIMING.repair, { type: 'crew', crew: 'recovering' });
    } else if (crew === 'recovering') {
      timer = go(TIMING.recover, { type: 'crew', crew: 'outbound' });
    } else if (crew === 'outbound') {
      timer = go(TIMING.outbound, { type: 'crew', crew: 'away' });
    }

    return () => clearTimeout(timer);
  }, [state, active]);

  return state;
};

export default useRepairCycle;
